import { useEffect } from 'react';

// Store
import useCustomerWishlist from '@src/hooks/useCustomerWishlist';
import useLocalWishlistStore from '@src/stores/localWishlistStore';

// Utils
import helpers from 'frontend/js/helpers';

export const WishlistManager = () => {
  const shopifyCustomerId = (window as any).customer.id;

  // Store
  // Local storage wishlist
  const localLists = useLocalWishlistStore((state) => state.localLists);
  const localListsValid = useLocalWishlistStore((state) => state.localListsValid);
  const resetLocalLists = useLocalWishlistStore((state) => state.resetLocalLists);
  const updateLocalList = useLocalWishlistStore((state) => state.updateLocalList);
  const validateLocalLists = useLocalWishlistStore((state) => state.validateLocalLists);
  // Customer wishlist
  const { fetchCustomerLists, updateCustomerList, createCustomerList } = useCustomerWishlist();

  // Add wishlist data to local storage if local storage has been removed
  // and there are existing customer wishlist metaobjects
  const updateLocalListsFromCustomerLists = (customerLists) => {
    if (customerLists) {
      for (const customerList of customerLists) {
        const matchingLocalList = localLists?.find((localList) => customerList.handle.includes(localList.listType));
        const newProducts = [...new Set([...(customerList.products || []), ...(matchingLocalList?.products || [])])];

        updateLocalList({
          listName: customerList.title,
          listType: customerList.handle.replace(`${shopifyCustomerId}-`, ''),
          products: newProducts
        });
      }
    }

    validateLocalLists();
  };

  // Update customer's metaobject & metafield when customer logs in
  // if there are updates
  const updateCustomerListsFromLocalLists = async (customerLists) => {
    if (shopifyCustomerId && localLists) {
      const newListIds: string[] = [];

      for (const localList of localLists) {
        const matchingCustomerList = customerLists?.find((customerList) =>
          customerList.handle.includes(localList.listType)
        );

        if (
          !matchingCustomerList ||
          (matchingCustomerList && !helpers.twoArraysWithSameValues(matchingCustomerList.products, localList.products))
        ) {
          const newProducts = [...new Set([...localList.products, ...(matchingCustomerList?.products || [])])];

          const updateListRes = await updateCustomerList({
            handle: `${shopifyCustomerId}-${localList.listType}`,
            listName: localList.listName,
            products: newProducts
          });

          if (!matchingCustomerList) {
            newListIds.push(updateListRes.id);
          }
        }
      }

      if (newListIds.length) {
        const updatedListIds = [...(customerLists?.map((wishlist) => wishlist.id) || []), ...newListIds];

        createCustomerList({ customerId: shopifyCustomerId, wishlistLists: updatedListIds });
      }
    }
  };

  // Sync wishlist data between local storage and metaobjects & metafield when customer logs in
  // This is done only once to prevent from reaching the Admin API limit
  useEffect(() => {
    if (!localListsValid && shopifyCustomerId) {
      const initWishlistManager = async () => {
        const fetchCustomerListsRes = await fetchCustomerLists({ customerId: shopifyCustomerId });
        updateLocalListsFromCustomerLists(fetchCustomerListsRes);
        updateCustomerListsFromLocalLists(fetchCustomerListsRes);
      };

      initWishlistManager();
    }
  }, [localListsValid, shopifyCustomerId]);

  // Clear wishlist data in local storage when customer logs out
  useEffect(() => {
    if (!shopifyCustomerId && localListsValid) {
      resetLocalLists();
      useLocalWishlistStore.persist.clearStorage();
    }
  }, []);

  return null;
};
